import { ExcelExportDto, PowerpointExportDto } from '@ecosystem/export-api';
import { actions, elements } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getWarningsBelongingOnTopOfChart, hasChartDataWithData } from './utils';

import classNames from 'classnames';
import { useTrackChartTooltipEvent } from '../../analytics/trackChartInteractions.hook';
import { mapDataVizForChartType, trackPageElementClicked } from '../../analytics/utils';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { DataVizToggle } from '../../components/dataviz.toggle';
import { Instructions } from '../../content/instructions';
import { DataContentProps } from '../../content/types';
import { useMobile } from '../../context/mobile/mobile.provider';
import { Loading } from '../../design/loading/Loading';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { WarningsDisplay } from '../../design/warning/warningsDisplay';
import { chartToPptData } from '../../export';
import { ExportButton, ExportButtonProps } from '../../export/export.button';
import { ExportHideout } from '../../export/ExportHideout';
import { useCorePageState } from '../../page/provider';
import { ComponentHeaderActionType } from '../../page/types';
import { splitIntoChunks } from '../../tools/arrays';
import {
  CollectionOptions,
  DataColumn,
  DataVizConfig,
  DataVizName,
  DataVizType,
  DataWarning,
  EventElementCategory,
  ExportFormat,
  ExportOptions,
  GenAiOptions,
  GenericWarning,
} from '../../types';
import AISignalsCard from '../aiCard/AISignalsCard';
import { GenAIStatus } from '../aiCard/GenAIStatus';
import { ScoreCardData } from '../aiCard/types';
import { useGenAI } from '../aiCard/useGenAI';
import { useGenAiTracking } from '../aiCard/useGenAiTracking';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import { DISABLED_CHART_MESSAGE } from '../constants';
import { ChartData } from '../data/common';
import { useMobileChart } from '../mobileChart/mobileChart.hook';
import { MobileOverflowMenu } from '../mobileMenu/mobileOverflowMenu';
import { createChartRef, getDisplayAs, getChartSliceSize, shouldDisableChart } from '../utils';
import { chartDataToExportData } from './chartDataToExportData';
import { CustomiseKpis } from './customise';
import { makeMultiKpiData } from './data';
import { MultiKpiChart } from './multiKpi.chart';
import { MultiKpiTable } from './multiKpi.table';
import { MultiKpiCollectionButton } from './MultiKpiCollectionButton';
import { MultiKpiCollectionButtonProps } from './types';

const { Button } = actions;
const { Icon } = elements;

interface ChartContentProps {
  chartData: ChartData[];
  xColumn: DataColumn | undefined;
  calculatePeriodCount: () => number;
  isTable: boolean;
  currency: string | undefined;
}

interface ExportModalContentProps extends ChartContentProps {
  insightSummary: { heading: string; text?: string }[] | Record<string, any>;
}

interface ExportDefaultContentProps extends ChartContentProps {
  globalWarnings: GenericWarning[] | undefined;
  warningsToBeDisplayedOnTop: DataWarning[] | undefined;
  warningSettings: { appendKpis: boolean };
}

interface ExportCardsProps extends ChartContentProps {
  exportModalOpen: boolean;
  chartSliceRefs: React.RefObject<HTMLDivElement[]>;
  setExportRefLength: (len: number) => void;
  insightSummary: { heading: string; text?: string }[] | Record<string, any>;
  globalWarnings: GenericWarning[] | undefined;
  warningsToBeDisplayedOnTop: DataWarning[] | undefined;
  warningSettings: { appendKpis: boolean };
}

const ExportModalContent = ({
  insightSummary,
  isTable,
  chartData,
  xColumn,
  calculatePeriodCount,
  currency,
}: ExportModalContentProps) => {
  return (
    <>
      <AISignalsCard insightSummary={insightSummary} />
      {isTable ? (
        <MultiKpiTable
          isEnrichmentLoading={false}
          periodCount={calculatePeriodCount()}
          data={chartData}
          xColumn={xColumn}
        />
      ) : (
        <MultiKpiChart data={chartData} currency={currency} trackTooltipEvent={undefined} />
      )}
    </>
  );
};

const ExportDefaultContent = ({
  globalWarnings,
  warningsToBeDisplayedOnTop,
  warningSettings,
  isTable,
  chartData,
  xColumn,
  calculatePeriodCount,
  currency,
}: ExportDefaultContentProps) => {
  return (
    <>
      {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
      <WarningsDisplay warnings={warningsToBeDisplayedOnTop} settings={warningSettings} />
      {isTable ? (
        <MultiKpiTable
          isEnrichmentLoading={false}
          periodCount={calculatePeriodCount()}
          data={chartData}
          xColumn={xColumn}
        />
      ) : (
        <MultiKpiChart data={chartData} currency={currency} trackTooltipEvent={undefined} />
      )}
    </>
  );
};

const ExportCards = ({
  chartData,
  exportModalOpen,
  chartSliceRefs,
  setExportRefLength,
  insightSummary,
  xColumn,
  calculatePeriodCount,
  globalWarnings,
  warningsToBeDisplayedOnTop,
  warningSettings,
  isTable,
  currency,
}: ExportCardsProps) => {
  const sliceSize = isTable ? 5 : getChartSliceSize(chartData?.length, chartData[0]?.dataLabels?.length);
  return (
    <>
      {splitIntoChunks<ChartData>(chartData, sliceSize).map((chartDataSlice, idx, arr) => {
        const uniqueKey = `export-hideout-${idx}`;
        return (
          <ExportHideout key={uniqueKey}>
            <div ref={createChartRef(idx, arr.length, setExportRefLength, chartSliceRefs)}>
              {exportModalOpen ? (
                <ExportModalContent
                  insightSummary={insightSummary}
                  isTable={isTable}
                  chartData={chartDataSlice}
                  xColumn={xColumn}
                  calculatePeriodCount={calculatePeriodCount}
                  currency={currency}
                />
              ) : (
                <ExportDefaultContent
                  globalWarnings={globalWarnings}
                  warningsToBeDisplayedOnTop={warningsToBeDisplayedOnTop}
                  warningSettings={warningSettings}
                  isTable={isTable}
                  chartData={chartDataSlice}
                  xColumn={xColumn}
                  calculatePeriodCount={calculatePeriodCount}
                  currency={currency}
                />
              )}
            </div>
          </ExportHideout>
        );
      })}
    </>
  );
};

function MultiKpi({
  data,
  isLoading,
  isEnrichmentLoading,
  enrichmentError,
  instructions = {},
  componentSettings,
  componentMetadata: componentMetaData,
  ComponentActionsContainer,
  collection,
  paramsForData,
  paramsForContext,
  trackEvent,
  updateSettings,
  componentHeadingTitle,
  proposition,
}: DataContentProps) {
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [dataStamp, setDataStamp] = useState<number>();
  const [chartData, setChartData] = useState<ChartData[] | undefined>();
  const [warnings, setWarnings] = useState<DataWarning[]>();
  const [currency, setCurrency] = useState<string>();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportRefLength, setExportRefLength] = useState<number>(0);
  const [isChartDisabled, setIsChartDisabled] = useState<boolean>(false);

  const chartSliceRefs = useRef<HTMLDivElement[]>([]);
  const { dsl, page, context, data: dataInfo, collection: collectionInfo, globalWarnings } = useCorePageState();

  const { isMobile } = useMobile();
  const { chartFullscreen, setChartFullscreen, MobileChartButton } = useMobileChart();

  const allColumns = data?.getAllColumns() ?? [];
  const isMultiKpiLoading = isLoading || isEnrichmentLoading;

  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];

  useOnCollectionExportReady({
    collection,
    dsl,
    chartSliceRefs,
    componentSettings,
    allColumns,
    componentHeadingTitle,
    isDataLoading: isMultiKpiLoading,
    refLength: exportRefLength,
  });

  const myInstructions = new Instructions(instructions, componentSettings);

  const xColumnName = myInstructions.getString('xColumn');
  const selectedKpis = myInstructions.getStringList('kpis');
  const maxKpis = myInstructions.getNumber('maxKpis', 0);
  const kpisParamName = myInstructions.isFromSettings('kpis') ? myInstructions.getSettingsName('kpis') : undefined;
  const exportOptions = myInstructions.getObject('export') as ExportOptions;
  const collectionOptions = myInstructions.getObject('collection') as CollectionOptions;
  const genAiOptions = myInstructions.getObject('genAi') as GenAiOptions;

  const { trackGenAiClicked } = useGenAiTracking(trackEvent, componentSettings, componentMetaData);
  const { aiEnabled, aiLoading, error, insightSummary, requestId, resetGenAI, runGenAI } = useGenAI({
    chartData: chartData as ScoreCardData[],
    config: genAiOptions,
    params: paramsForData,
    trackGenAiClicked,
  });

  const [displayAs, setDisplayAs] = useState<DataVizType>(getDisplayAs(componentSettings));

  const canEditKpis = kpisParamName !== undefined;
  const dataColumns: DataColumn[] = data ? [...data.getDataColumns(), ...data.getAvailableColumns()] : [];

  const xColumn = dataColumns.find(col => col.name === xColumnName);

  const { trackTooltipEvent } = useTrackChartTooltipEvent(
    componentSettings,
    !!collection,
    collectionOptions?.chartName
  );

  const updateData = () => {
    if (!data) {
      return;
    }
    const dataWarnings = data.getDataWarnings(selectedKpis);

    const selectedKpiColumns = selectedKpis
      .map(kpi => {
        return dataColumns.find(col => col.name === kpi);
      })
      .filter(column => !!column);

    const chartData = makeMultiKpiData(data, {
      kpis: selectedKpis,
      requestedColumns: selectedKpiColumns,
      xColumnName,
      dataWarnings,
      sharedBrands,
    });

    if (chartData) {
      setWarnings(dataWarnings);
      setChartData(chartData);
      setCurrency(data.getCurrency());
      setDataStamp(data.stamp);

      const isDisabled = shouldDisableChart(chartData[0]?.dataSets);
      setIsChartDisabled(isDisabled);
      if (isDisabled) {
        setDisplayAs(DataVizType.table);
      }
    }
  };

  const handleDataVizChange = useCallback(
    (value: DataVizType) => {
      setDisplayAs(value);
      updateSettings({
        ...componentSettings,
        dataViz: [value],
      });
      trackPageElementClicked(trackEvent, mapDataVizForChartType(value), {
        elementCategory: mapDataVizForChartType(value) as EventElementCategory,
      });
    },
    [componentSettings, updateSettings, trackEvent]
  );

  if (!componentSettings.dataViz?.length) {
    updateSettings({
      ...componentSettings,
      dataViz: [displayAs],
    });
  }

  const handleEditClick = () => {
    setShowEdit(true);
    trackPageElementClicked(trackEvent, 'Click custom KPIs button');
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === dataStamp) {
      return;
    }
    updateData();
    resetGenAI();
  }, [data]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    updateData();
  }, [selectedKpis]);

  useEffect(() => {
    if (!exportModalOpen) {
      chartSliceRefs.current = [];
    }
  }, [exportModalOpen]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  if (!data) {
    return null;
  }

  const getExportData = async (
    exportFormat: ExportFormat
  ): Promise<PowerpointExportDto | ExcelExportDto | undefined> => {
    if (exportFormat === ExportFormat.Xlsx) {
      return chartDataToExportData(exportFormat, exportOptions, chartData, xColumn, currency, globalWarnings);
    }
    if (exportFormat === ExportFormat.Pptx) {
      return await chartToPptData(
        exportOptions,
        dsl,
        '',
        chartSliceRefs.current,
        componentSettings,
        allColumns,
        componentHeadingTitle
      );
    }
    return undefined;
  };

  const warningsToBeDisplayedOnTop =
    displayAs !== 'table' ? getWarningsBelongingOnTopOfChart(chartData, warnings) : warnings;
  const warningSettings = { appendKpis: true };

  const calculatePeriodCount = (): number => {
    const periodCount = data?.getGroupedSets().find(set => !!set.data)?.type.timeFrame.periods.length;
    return periodCount || 0;
  };

  if (!isMultiKpiLoading && !hasChartDataWithData(chartData)) {
    if (collection) collection.onExportReady(null);
    return <EmptyState />;
  }
  const renderTableBlock = () => (
    <>
      <MultiKpiTable
        isEnrichmentLoading={isEnrichmentLoading}
        periodCount={calculatePeriodCount()}
        data={chartData}
        xColumn={xColumn}
        chartFullscreen={chartFullscreen}
      />
      {(collection || exportModalOpen) && (
        <ExportCards
          chartData={chartData}
          exportModalOpen={exportModalOpen}
          chartSliceRefs={chartSliceRefs}
          setExportRefLength={setExportRefLength}
          insightSummary={insightSummary}
          xColumn={xColumn}
          calculatePeriodCount={calculatePeriodCount}
          globalWarnings={globalWarnings}
          warningsToBeDisplayedOnTop={warningsToBeDisplayedOnTop}
          warningSettings={warningSettings}
          isTable={true}
          currency={currency}
        />
      )}
    </>
  );

  const renderChartBlock = () => (
    <>
      <MultiKpiChart
        isEnrichmentLoading={isEnrichmentLoading}
        isLoading={isLoading}
        data={chartData}
        currency={currency}
        enrichmentError={enrichmentError}
        trackTooltipEvent={trackTooltipEvent}
        chartFullscreen={chartFullscreen}
      />
      {(collection || exportModalOpen) && (
        <ExportCards
          chartData={chartData}
          exportModalOpen={exportModalOpen}
          chartSliceRefs={chartSliceRefs}
          setExportRefLength={setExportRefLength}
          insightSummary={insightSummary}
          xColumn={xColumn}
          calculatePeriodCount={calculatePeriodCount}
          globalWarnings={globalWarnings}
          warningsToBeDisplayedOnTop={warningsToBeDisplayedOnTop}
          warningSettings={warningSettings}
          isTable={false}
          currency={currency}
        />
      )}
    </>
  );

  const dataVizConfig: DataVizConfig[] = [
    {
      type: DataVizType.chart,
      isDisabled: isChartDisabled,
      tooltipContent: DISABLED_CHART_MESSAGE,
      name: DataVizName.chart,
    },
    {
      type: DataVizType.table,
      isDisabled: false,
      name: DataVizName.table,
    },
  ];

  const multiKpiCollectionButtonProps: MultiKpiCollectionButtonProps = {
    collectionOptions,
    collectionInfo,
    proposition,
    paramsForData,
    paramsForContext,
    page,
    context,
    dataInfo,
    componentSettings,
    isDisabled: isMultiKpiLoading,
  };

  const exportButtonProps: ExportButtonProps = {
    trackingSourceData: data,
    componentSettings,
    componentMetadata: componentMetaData,
    disabled: isMultiKpiLoading,
    exportFormats: [ExportFormat.Xlsx, ExportFormat.Pptx],
    getExportData,
    exportContentRef: chartSliceRefs,
    onModalVisibilityChange: isVisible => setExportModalOpen(isVisible),
    trackEvent,
  };

  const renderActionsContainer = () => {
    return (
      <>
        {aiEnabled && (
          <Button
            disabled={isMultiKpiLoading || insightSummary !== null}
            variant="gradient"
            mode="secondary"
            label="Smart Insights"
            testId="ai-btn"
            isLoading={aiLoading}
            onClick={runGenAI}
          >
            <Button.Icon>
              <Icon.icons.AIGradient color={isMultiKpiLoading && 'inactive'} />
            </Button.Icon>
          </Button>
        )}
        <MultiKpiCollectionButton {...multiKpiCollectionButtonProps} actionType={ComponentHeaderActionType.Button} />
        {exportOptions && <ExportButton {...exportButtonProps} actionType={ComponentHeaderActionType.Button} />}
        {canEditKpis && (
          <Button
            disabled={isMultiKpiLoading}
            onClick={handleEditClick}
            mode="secondary"
            label="select"
            testId="edit-kpi-btn"
          >
            <Button.Icon>
              <Button.Icon.icons.Option />
            </Button.Icon>
          </Button>
        )}
        <DataVizToggle options={dataVizConfig} value={displayAs} onChange={handleDataVizChange} />
      </>
    );
  };

  const renderMobileActionsContainer = () => {
    return (
      <MobileOverflowMenu
        isLoading={isMultiKpiLoading}
        isAiEnabled={aiEnabled}
        dataOptions={dataVizConfig}
        canExportDataSet={!!exportOptions}
        canEditKpis={canEditKpis}
        handleEditKpi={handleEditClick}
        runGenAI={runGenAI}
        canToggleDataViz={true}
        handleDataVizChange={handleDataVizChange}
        selectedDataViz={displayAs}
        multiKpiCollectionButtonProps={multiKpiCollectionButtonProps}
        exportButtonProps={exportButtonProps}
      />
    );
  };

  return (
    <>
      <GenAIStatus
        error={error}
        aiEnabled={aiEnabled}
        aiLoading={aiLoading}
        endpoint={genAiOptions?.endpoint}
        requestId={requestId}
        insightSummary={insightSummary}
        onCloseSummary={resetGenAI}
      />
      <WarningsDisplay warnings={warningsToBeDisplayedOnTop} settings={warningSettings} />
      {chartData && ComponentActionsContainer && (
        <ComponentActionsContainer>
          {!isMobile ? renderActionsContainer() : renderMobileActionsContainer()}
        </ComponentActionsContainer>
      )}
      <div className={classNames({ 'chart-full-screen': chartFullscreen })}>
        {isMobile && !isLoading && (
          <MobileChartButton chartFullscreen={chartFullscreen} setChartFullscreen={setChartFullscreen} />
        )}
        <div
          className={classNames('multi-kpi relative w-full max-w-full', {
            'pt-4': !chartFullscreen || displayAs === 'table',
          })}
        >
          <Loading isActive={isLoading} />
          {displayAs === 'table' && chartData && renderTableBlock()}
          {displayAs !== 'table' && chartData && renderChartBlock()}
          {chartData && showEdit && (
            <CustomiseKpis
              onClose={() => setShowEdit(false)}
              currentKpis={selectedKpis}
              data={data}
              maxCount={maxKpis}
              paramName={kpisParamName}
              updateSettings={updateSettings}
              trackEvent={trackEvent}
            />
          )}
        </div>
      </div>
    </>
  );
}

export { MultiKpi };
